'use client';

import React, { useContext, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { DateTime } from 'luxon';
import { SessionContext } from '@benzinga/session-context';
import { QuotesManager, type DelayedQuote } from '@benzinga/quotes-manager';
import { PriceCell } from './PriceCell';
import { calendarHref } from './landingSidebarLinks';

export interface UnifiedEvent {
  id: string;
  slug: string;
  eventTypeLabel: string;
  ticker: string;
  company: string;
  date: string;
  time: string | null;
  importance: number | null;
  sortTs: number;
}

interface Props {
  futureEvents: UnifiedEvent[];
  pastEvents: UnifiedEvent[];
}

type QuoteMap = Record<string, DelayedQuote | undefined>;

const ET_ZONE = 'America/New_York';

function fmtDate(date: string): string {
  const dt = DateTime.fromISO(date, { zone: ET_ZONE });
  return dt.isValid ? dt.toFormat('LLL d, yyyy') : date;
}

function fmtTime(time: string | null): string {
  if (!time) return '';
  const trimmed = time.length >= 5 ? time.slice(0, 5) : time;
  const dt = DateTime.fromFormat(trimmed, 'HH:mm', { zone: ET_ZONE });
  return dt.isValid ? `${dt.toFormat('h:mm a')} ET` : trimmed;
}

const ImportanceDots: React.FC<{ value: number | null }> = ({ value }) => {
  if (value === null) {
    return (
      <span className="text-bzgray-400" aria-label="Importance not available">
        <span aria-hidden="true">—</span>
      </span>
    );
  }
  return (
    <span className="inline-flex gap-0.5" aria-label={`Importance ${value} out of 5`}>
      {[1, 2, 3, 4, 5].map(i => (
        <span
          key={i}
          className={`inline-block h-2 w-2 rounded-full ${i <= value ? 'bg-bzblue-1000' : 'bg-bzgray-300'}`}
        />
      ))}
    </span>
  );
};

const UnifiedRow: React.FC<{ event: UnifiedEvent; quote: DelayedQuote | undefined; isLoading: boolean }> = ({
  event,
  quote,
  isLoading,
}) => {
  const displayCompany = event.company === event.ticker && quote?.name ? quote.name : event.company;
  return (
    <tr className="border-b border-bzgray-200 hover:bg-bzgray-50 w-full last:border-b-0">
      <td className="whitespace-nowrap px-3 py-2.5 text-sm text-bzgray-700">
        {event.time ? fmtTime(event.time) : '-'}
      </td>
      <td className="whitespace-nowrap px-3 py-2.5 text-sm text-bzgray-800">{fmtDate(event.date)}</td>
      <td className="whitespace-nowrap px-3 py-2.5 text-sm font-medium">
        <Link href={`/quote/${encodeURIComponent(event.ticker)}`} className="text-bzblue-1000 hover:underline">
          {event.ticker}
        </Link>
      </td>
      <td className="whitespace-nowrap px-3 py-2.5 text-sm text-bzgray-800">
        <div className="max-w-[180px] truncate" title={displayCompany}>
          {displayCompany}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-2.5 text-sm">
        <PriceCell quote={quote} isLoading={isLoading} />
      </td>
      <td className="whitespace-nowrap px-3 py-2.5 text-sm">
        <Link href={calendarHref(event.slug)} className="font-medium text-bzblue-1000 hover:underline">
          {event.eventTypeLabel}
        </Link>
      </td>
      <td className="whitespace-nowrap px-3 py-2.5 text-sm">
        <ImportanceDots value={event.importance} />
      </td>
    </tr>
  );
};

const UnifiedTable: React.FC<{ events: UnifiedEvent[]; quotes: QuoteMap; isLoadingQuotes: boolean }> = ({
  events,
  quotes,
  isLoadingQuotes,
}) => (
  <div className="overflow-x-auto rounded-md border border-bzgray-300 bg-white">
    <table className="w-full table-auto" aria-label="Upcoming and recent financial events">
      <thead className="bg-bzgray-100">
        <tr>
          <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-bzgray-700">
            Time
          </th>
          <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-bzgray-700">
            Date
          </th>
          <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-bzgray-700">
            Ticker
          </th>
          <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-bzgray-700">
            Company
          </th>
          <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-bzgray-700">
            Price (Chg %)
          </th>
          <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-bzgray-700">
            Event Type
          </th>
          <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-bzgray-700">
            Importance
          </th>
        </tr>
      </thead>
      <tbody>
        {events.length === 0 ? (
          <tr>
            <td colSpan={7} className="px-3 py-6 text-center text-sm text-bzgray-600">
              No events to display.
            </td>
          </tr>
        ) : (
          events.map(ev => <UnifiedRow key={ev.id} event={ev} quote={quotes[ev.ticker]} isLoading={isLoadingQuotes} />)
        )}
      </tbody>
    </table>
  </div>
);

export const CalendarLandingContent: React.FC<Props> = ({ futureEvents, pastEvents }) => {
  const unified = useMemo(() => [...futureEvents, ...pastEvents], [futureEvents, pastEvents]);
  const tickersKey = useMemo(
    () =>
      Array.from(new Set(unified.map(e => e.ticker)))
        .sort()
        .join(','),
    [unified],
  );
  const session = useContext(SessionContext);
  const [quotes, setQuotes] = useState<QuoteMap>({});
  // True only until the first successful fetch — subsequent refetches keep showing the
  // last known prices instead of flickering back to "…".
  const [hasLoadedQuotes, setHasLoadedQuotes] = useState(false);
  const isLoadingQuotes = !hasLoadedQuotes && tickersKey.length > 0;

  useEffect(() => {
    // Wait for session — don't flip the loaded latch on this branch, otherwise a later
    // re-run (once session resolves) would skip its loading state and snap straight from
    // "—" to the populated price.
    if (!session) return;
    if (tickersKey.length === 0) {
      // No tickers to fetch — genuinely nothing to load.
      setHasLoadedQuotes(true);
      return;
    }
    let cancelled = false;
    const tickers = tickersKey.split(',');
    const quotesManager = session.getManager(QuotesManager);
    quotesManager
      .getDelayedQuotes(tickers)
      .then(result => {
        if (cancelled) return;
        if (result.ok) {
          // Defensive: re-key by upper-cased symbol so lookups by ev.ticker (also uppercase)
          // never miss due to API casing differences.
          const upperKeyed: QuoteMap = {};
          for (const [k, v] of Object.entries(result.ok)) {
            upperKeyed[k.toUpperCase()] = v;
          }
          setQuotes(upperKeyed);
        }
        setHasLoadedQuotes(true);
      })
      .catch(() => {
        if (cancelled) return;
        setHasLoadedQuotes(true);
      });
    return () => {
      cancelled = true;
    };
  }, [session, tickersKey]);

  return <UnifiedTable events={unified} quotes={quotes} isLoadingQuotes={isLoadingQuotes} />;
};
